/**
 * Material Domain Rules
 *
 * Business logic for material categorization and container size restrictions.
 * These rules are based on weight limits and safety requirements.
 *
 * Weight Limits & Safety Rationale:
 * - Heavy materials (concrete, asphalt, dirt, rock) can only use 10-yard containers
 *   because their density creates unsafe weight conditions in larger containers
 * - C&D materials (construction & demolition debris) are restricted from 10-yard
 *   to reserve those containers for heavy materials and optimize fleet utilization
 * - Light materials (household, green waste, recyclables) can use any size
 */

export type MaterialCategory = 'heavy' | 'cd' | 'light';
export type ContainerSize = '10Y' | '20Y' | '30Y' | '40Y';

/**
 * Heavy materials that are restricted to 10-yard containers only.
 * These materials exceed safe weight limits when loaded in larger containers.
 */
const HEAVY_MATERIALS: readonly string[] = [
  'concrete',
  'asphalt',
  'dirt_soil',
  'rock_gravel',
] as const;

/**
 * Construction & Demolition materials that are restricted from 10-yard containers.
 * These materials work best in 20Y+ containers, and we reserve 10Y for heavy materials.
 */
const CD_MATERIALS: readonly string[] = [
  'mixed_cd',
  'wood',
  'drywall',
  'roofing',
] as const;

/**
 * All possible container sizes in order of capacity
 */
const ALL_SIZES: readonly ContainerSize[] = ['10Y', '20Y', '30Y', '40Y'] as const;

/**
 * Container sizes available for heavy materials (weight-restricted)
 */
const HEAVY_MATERIAL_SIZES: readonly ContainerSize[] = ['10Y'] as const;

/**
 * Container sizes available for C&D materials (excludes 10Y to reserve for heavy materials)
 */
const CD_MATERIAL_SIZES: readonly ContainerSize[] = ['20Y', '30Y', '40Y'] as const;

export const MaterialRules = {
  /**
   * Get allowed container sizes for a material type
   *
   * @param materialType - The material type identifier (e.g., 'concrete', 'mixed_cd')
   * @returns Array of allowed container sizes for this material
   *
   * @example
   * ```ts
   * MaterialRules.getAllowedSizes('concrete') // ['10Y']
   * MaterialRules.getAllowedSizes('mixed_cd') // ['20Y', '30Y', '40Y']
   * MaterialRules.getAllowedSizes('household') // ['10Y', '20Y', '30Y', '40Y']
   * ```
   */
  getAllowedSizes(materialType: string): ContainerSize[] {
    // Heavy materials → 10Y only (too heavy for larger containers)
    if (HEAVY_MATERIALS.includes(materialType)) {
      return [...HEAVY_MATERIAL_SIZES];
    }

    // C&D materials → 20Y, 30Y, 40Y only (save 10Y for heavy materials)
    if (CD_MATERIALS.includes(materialType)) {
      return [...CD_MATERIAL_SIZES];
    }

    // Other materials → All sizes available
    return [...ALL_SIZES];
  },

  /**
   * Check if a material is categorized as heavy
   *
   * Heavy materials are those with high density that create unsafe weight
   * conditions when loaded in larger containers.
   *
   * @param materialType - The material type identifier
   * @returns True if the material is categorized as heavy
   *
   * @example
   * ```ts
   * MaterialRules.isHeavyMaterial('concrete') // true
   * MaterialRules.isHeavyMaterial('mixed_cd') // false
   * ```
   */
  isHeavyMaterial(materialType: string): boolean {
    return HEAVY_MATERIALS.includes(materialType);
  },

  /**
   * Check if a material is categorized as C&D (Construction & Demolition)
   *
   * @param materialType - The material type identifier
   * @returns True if the material is categorized as C&D
   *
   * @example
   * ```ts
   * MaterialRules.isCDMaterial('mixed_cd') // true
   * MaterialRules.isCDMaterial('concrete') // false
   * ```
   */
  isCDMaterial(materialType: string): boolean {
    return CD_MATERIALS.includes(materialType);
  },

  /**
   * Get the weight category of a material
   *
   * @param materialType - The material type identifier
   * @returns The material's weight category
   *
   * @example
   * ```ts
   * MaterialRules.getMaterialCategory('concrete') // 'heavy'
   * MaterialRules.getMaterialCategory('mixed_cd') // 'cd'
   * MaterialRules.getMaterialCategory('household') // 'light'
   * ```
   */
  getMaterialCategory(materialType: string): MaterialCategory {
    if (HEAVY_MATERIALS.includes(materialType)) {
      return 'heavy';
    }
    if (CD_MATERIALS.includes(materialType)) {
      return 'cd';
    }
    return 'light';
  },

  /**
   * Get a user-friendly restriction message for a material
   *
   * Returns a human-readable explanation of why certain container sizes
   * are restricted for this material type.
   *
   * @param materialType - The material type identifier
   * @returns A restriction message, or null if no restrictions apply
   *
   * @example
   * ```ts
   * MaterialRules.getRestrictionMessage('concrete')
   * // "Heavy materials are too heavy for larger containers. Only 10-yard containers..."
   *
   * MaterialRules.getRestrictionMessage('household')
   * // null
   * ```
   */
  getRestrictionMessage(materialType: string): string | null {
    const category = this.getMaterialCategory(materialType);

    if (category === 'heavy') {
      return 'Heavy materials are too heavy for larger containers. Only 10-yard containers are available to ensure safe weight limits.';
    }

    if (category === 'cd') {
      return 'Construction & demolition materials work best in 20-yard or larger containers. We reserve 10-yard containers for heavy materials like concrete and dirt.';
    }

    return null;
  },
} as const;
